import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Typography,
  Alert,
} from '@mui/material';
import {
  useGetFieldDefinitionsQuery,
  useGetFieldValuesQuery,
  useSaveFieldValuesMutation,
} from '../../app/services/customFields';

export default function CustomFieldsForm({
  maintenanceId,
  machineMaintenanceId,
  onValuesChange,
  submitTrigger,
}) {
  const { t } = useTranslation();
  const [fieldValues, setFieldValues] = useState({});
  const [errors, setErrors] = useState({});

  const { data: fieldDefinitions = [] } = useGetFieldDefinitionsQuery(maintenanceId, {
    skip: !maintenanceId,
  });

  const { data: savedValues = [] } = useGetFieldValuesQuery(machineMaintenanceId, {
    skip: !machineMaintenanceId,
  });

  const [saveValues] = useSaveFieldValuesMutation();

  // Initialize form with saved values
  useEffect(() => {
    if (savedValues.length > 0) {
      const values = {};
      savedValues.forEach((val) => {
        values[val.fieldDefinitionId] = val.value;
      });
      setFieldValues(values);
    }
  }, [savedValues]);

  // Handle value changes
  const handleValueChange = (fieldId, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    // Clear error for this field
    if (errors[fieldId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
    // Notify parent
    if (onValuesChange) {
      onValuesChange({ ...fieldValues, [fieldId]: value });
    }
  };

  const validate = useCallback(() => {
    const newErrors = {};

    fieldDefinitions.forEach((field) => {
      const value = fieldValues[field.id];

      // Check required fields
      if (field.is_required && (!value || value.trim() === '')) {
        newErrors[field.id] = t('customFields.errors.fieldRequired', {
          name: field.field_name,
        });
      }

      // Validate numeric fields
      if (field.field_type === 'numeric' && value && isNaN(parseFloat(value))) {
        newErrors[field.id] = t('customFields.errors.mustBeNumber');
      }

      // Validate radio options
      if (field.field_type === 'radio' && value && field.field_options) {
        if (!field.field_options.includes(value)) {
          newErrors[field.id] = t('customFields.errors.invalidOption');
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [fieldDefinitions, fieldValues, t]);

  const handleSave = useCallback(async () => {
    if (!validate()) {
      return false;
    }

    if (!machineMaintenanceId) {
      console.error('No machineMaintenanceId provided');
      return false;
    }

    try {
      const values = fieldDefinitions.map((field) => ({
        fieldDefinitionId: field.id,
        value: fieldValues[field.id] || '',
      }));

      await saveValues({
        machineMaintenanceId,
        values,
      }).unwrap();

      return true;
    } catch (error) {
      console.error('Error saving custom field values:', error);
      setErrors({ submit: error?.data?.message || t('customFields.errors.saveFailed') });
      return false;
    }
  }, [validate, machineMaintenanceId, fieldDefinitions, fieldValues, saveValues, t]);

  // Validate and save when submit is triggered
  useEffect(() => {
    if (submitTrigger) {
      handleSave();
    }
  }, [submitTrigger, handleSave]);

  if (fieldDefinitions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('customFields.additionalInformation')}
      </Typography>

      {fieldDefinitions.map((field) => (
        <Box key={field.id} sx={{ mb: 2 }}>
          {field.field_type === 'text' && (
            <TextField
              label={field.field_name}
              value={fieldValues[field.id] || ''}
              onChange={(e) => handleValueChange(field.id, e.target.value)}
              required={field.is_required}
              error={!!errors[field.id]}
              helperText={errors[field.id]}
              fullWidth
            />
          )}

          {field.field_type === 'numeric' && (
            <TextField
              label={field.field_name}
              type="number"
              value={fieldValues[field.id] || ''}
              onChange={(e) => handleValueChange(field.id, e.target.value)}
              required={field.is_required}
              error={!!errors[field.id]}
              helperText={errors[field.id] || (field.unit ? `Unit: ${field.unit}` : '')}
              fullWidth
              InputProps={{
                endAdornment: field.unit && (
                  <Typography variant="body2" color="text.secondary">
                    {field.unit}
                  </Typography>
                ),
              }}
            />
          )}

          {field.field_type === 'checkbox' && (
            <FormControl error={!!errors[field.id]}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fieldValues[field.id] === 'true'}
                    onChange={(e) => handleValueChange(field.id, e.target.checked ? 'true' : 'false')}
                  />
                }
                label={field.field_name + (field.is_required ? ' *' : '')}
              />
              {errors[field.id] && (
                <Typography variant="caption" color="error">
                  {errors[field.id]}
                </Typography>
              )}
            </FormControl>
          )}

          {field.field_type === 'radio' && (
            <FormControl error={!!errors[field.id]} required={field.is_required} fullWidth>
              <FormLabel>{field.field_name}</FormLabel>
              <RadioGroup
                value={fieldValues[field.id] || ''}
                onChange={(e) => handleValueChange(field.id, e.target.value)}
              >
                {field.field_options &&
                  field.field_options.map((option) => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={<Radio />}
                      label={option}
                    />
                  ))}
              </RadioGroup>
              {errors[field.id] && (
                <Typography variant="caption" color="error">
                  {errors[field.id]}
                </Typography>
              )}
            </FormControl>
          )}
        </Box>
      ))}

      {errors.submit && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.submit}
        </Alert>
      )}
    </Box>
  );
}
